import { Tag, Button, Link, Callout, Avatar } from '@dev-spendesk/grapes';
import { useEffect, useId } from 'react';
import { Trans } from 'react-i18next';

import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import { type I18nKey, useTranslation } from 'common/hooks/useTranslation';
import { type Integration } from 'modules/integrations/types';
import {
  getHelpCenter,
  getIntegrationDescriptionKey,
  getIntegrationLogo,
  getIntegrationName,
  isKomboIntegration,
} from 'src/core/modules/integrations/utils';

type Props = {
  integration: Integration & {
    active: boolean;
    available: boolean;
  };
  selected?: boolean;
  selectIntegration: (integration: Integration) => void;
};

export function IntegrationBox(props: Props) {
  const { integration, selectIntegration, selected } = props;
  const { active, available, id, error } = integration;

  const { t } = useTranslation('global');

  const titleId = useId();
  const logo = getIntegrationLogo(id);
  const name = getIntegrationName(t, id);
  const helpCenter = getHelpCenter(id);
  const descriptionKey = getIntegrationDescriptionKey(id);

  useEffect(() => {
    if (selected) {
      selectIntegration(integration);
    }
  }, [selected]);

  const shouldRenderHrIntegrationsErrorCallout =
    active && isKomboIntegration(integration.id) && error === 'notConnected';

  const renderActiveStatusTag = () => {
    if (!error) {
      return (
        <Tag className="ml-auto" variant="success" iconName="circle-check">
          <Trans i18nKey="integration.tags.connected" />
        </Tag>
      );
    }

    switch (error) {
      case 'notConnected':
        return (
          <Tag className="ml-auto" variant="alert" iconName="hexagone-cross">
            <Trans i18nKey="integration.tags.notConnected" />
          </Tag>
        );
      case 'pendingAction':
        return (
          <Tag
            className="ml-auto"
            variant="warning"
            iconName="triangle-warning"
          >
            <Trans i18nKey="integration.tags.pendingAction" />
          </Tag>
        );
      case 'pendingReview':
        return (
          <Tag className="ml-auto" variant="info" iconName="circle-information">
            <Trans i18nKey="integration.tags.pendingReview" />
          </Tag>
        );
      default:
        return null;
    }
  };

  return (
    <article className="box flex flex-col gap-24" aria-labelledby={titleId}>
      <div className="flex flex-row items-center gap-8">
        <Avatar
          variant="square"
          text={name}
          src={logo}
          size={32}
          fallbackSrc={fallbackSupplierLogoSrc}
        />
        <h1 className="text-content-primary title-l" id={titleId}>
          {name}
        </h1>
        {active && renderActiveStatusTag()}
      </div>
      <div className="w-full grow text-content-secondary-bg-primary">
        {shouldRenderHrIntegrationsErrorCallout ? (
          <Callout
            title={t(
              'integration.errors.hrIntegrationAuthenticationFailed.details',
            )}
            variant="alert"
          />
        ) : (
          <>
            <Trans i18nKey={descriptionKey} values={{ name }}>
              text
              <Link
                className="text-content-primary"
                isExternal
                href={helpCenter}
                rel="noreferrer"
              >
                link
              </Link>
              text
            </Trans>
            {error === 'pendingSync' && (
              <Callout
                className="mt-16"
                title={t('integration.errors.pendingSync.title')}
                variant="info"
                iconName="arrow-path"
              />
            )}
          </>
        )}
      </div>
      <div className="w-full">
        {available ? (
          <Button
            variant="secondaryNeutral"
            fit="parent"
            iconName={active ? 'adjustments-horizontal' : 'arrow-path'}
            text={t(active ? 'integration.manage' : 'integration.connect')}
            onClick={() => selectIntegration(integration)}
          />
        ) : (
          error && (
            <Callout
              title={t(integrationErrorToI18nKey(error))}
              variant="info"
            />
          )
        )}
      </div>
    </article>
  );
}

const integrationErrorToI18nKey = (errors: Integration['error']): I18nKey => {
  switch (errors) {
    case 'wrongPlan':
      return 'integration.errors.wrongPlan.details';
    case 'notConnected':
      return 'integration.errors.notConnected.details';
    case 'pendingSync':
      return 'integration.errors.pendingSync.details';
    case 'pendingAction':
      return 'integration.errors.pendingAction.details';
    case 'hrIntegrationAlreadyConnected':
      return 'integration.errors.hrIntegrationAlreadyConnected.details';
    case 'unknown':
      return 'integration.errors.unknown.details';
    default:
      return 'integration.errors.unknown.details';
  }
};
